/**
 * Concurrency + backpressure gate for a batch of schedule calls.
 *
 * WHAT THIS IS
 *   A small admission controller. Callers hand it a thunk that performs one
 *   `POST /v1/api/tasks/schedule`; it runs at most `concurrency` of them at a
 *   time, and when ANY call comes back with a `Retry-After` it pauses the whole
 *   batch until that instant. It owns no HTTP and no UI.
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   Today the client fans out schedule calls with an uncapped `Promise.all` over
 *   every card (CORE-PIPELINE-REFERENCE §10). One user's 340-file batch
 *   therefore arrived at professorx as 340 concurrent schedule requests, each of
 *   which is ~10 Mongo writes (object-log, workflow, 3 tasks, taskIds + logs
 *   push) before the optimistic-lock loop even starts, on a primary that was
 *   already stalled. There is no per-edge concurrency cap anywhere either
 *   (correction 11), so nothing upstream absorbed it.
 *
 *   Two rules follow, and this module is both:
 *     - cap the fan-out at 4-6 (plan §4.2; 4 matches the existing local-upload
 *       cap, correction 22);
 *     - make Retry-After a SHARED gate. Per-file backoff is useless here: every
 *       file in the batch is talking to the same overloaded professorx, so if
 *       one call is told to wait 30 s, the other 339 must wait too. The pause is
 *       held on the gate, not on the card.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4.2 ("Cap concurrent schedule calls at 4-6 with a
 *   queue; one **shared** Retry-After gate per batch, not per file"),
 *   corrections 11, 22. CORE-PIPELINE-REFERENCE.md §10 ("Schedule").
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState`: one instance lives on
 * `scheduleCoordinator`, and every schedule call goes through `gate.run(...)`.
 * With the flag OFF the convert path still fans out uncapped.
 */

/** Plan §4.2, matching the existing local-upload cap of 4. */
export const DEFAULT_SCHEDULE_CONCURRENCY = 4;

/**
 * Upper bound. Anything higher is clamped, not rejected. Raised from 6 to 8 for code 10
 * (ENFORCEMENT-POLICY.md §6.2 item 10, ruling R6): the PRO plan sells "8 concurrent video tasks".
 */
export const MAX_SCHEDULE_CONCURRENCY = 8;

/**
 * Code 10 — client pacing at the SOLD video concurrency (/product: Basic "4 concurrent video
 * tasks", Pro "8"). A client behaviour, not a server limit (ruling R6: no per-user concurrency
 * limit server-side). FREE keeps today's 4-slot gate. Non-video work is not paced by tier: it keeps
 * `DEFAULT_SCHEDULE_CONCURRENCY`, byte for byte today's behaviour.
 */
export type ScheduleTier = 'FREE' | 'BASIC' | 'PRO';
export const VIDEO_SCHEDULE_CONCURRENCY_BY_TIER: Readonly<Record<ScheduleTier, number>> = {
    FREE: 4,
    BASIC: 4,
    PRO: 8,
};

export function videoConcurrencyForTier(tier: ScheduleTier | null | undefined): number {
    return VIDEO_SCHEDULE_CONCURRENCY_BY_TIER[tier ?? 'FREE'] ?? DEFAULT_SCHEDULE_CONCURRENCY;
}

/** What a queued call is: video work follows the tier lane, everything else today's gate. */
export type ScheduleWorkKind = 'video' | 'other';

export interface BatchScheduleGateOptions {
    /** Slots. Clamped to [1, MAX_SCHEDULE_CONCURRENCY]. */
    concurrency?: number;
    /** Slots for VIDEO work (code 10). Defaults to `concurrency`. Clamped the same way. */
    videoConcurrency?: number;
    /** Injectable clock; defaults to `Date.now`. */
    now?: () => number;
    /** Injectable timer, so tests can drive it; defaults to `setTimeout`. */
    setTimer?: (fn: () => void, ms: number) => any;
    clearTimer?: (handle: any) => void;
}

interface QueueEntry<T> {
    task: () => Promise<T>;
    kind: ScheduleWorkKind;
    resolve: (value: T) => void;
    reject: (reason?: unknown) => void;
}

function clampConcurrency(requested: number): number {
    return Math.min(Math.max(1, Math.floor(requested)), MAX_SCHEDULE_CONCURRENCY);
}

export class BatchScheduleGate {
    readonly concurrency: number;

    private readonly now: () => number;
    private readonly setTimer: (fn: () => void, ms: number) => any;
    private readonly clearTimer: (handle: any) => void;

    private readonly queue: QueueEntry<any>[] = [];
    private running = 0;
    private pausedUntil: number | null = null;
    private timerHandle: any = null;
    private videoSlots: number;
    private hiddenFlag = false;

    constructor(options: BatchScheduleGateOptions = {}) {
        this.concurrency = clampConcurrency(options.concurrency ?? DEFAULT_SCHEDULE_CONCURRENCY);
        this.videoSlots = clampConcurrency(options.videoConcurrency ?? this.concurrency);
        this.now = options.now ?? (() => Date.now());
        this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
    }

    /** Calls currently in flight. */
    get inFlight(): number {
        return this.running;
    }

    /** Calls admitted to the gate but not yet started. */
    get queued(): number {
        return this.queue.length;
    }

    /** The instant the shared pause lifts, or null when the gate is open. */
    get pausedUntilMs(): number | null {
        return this.pausedUntil;
    }

    get isPaused(): boolean {
        return this.pausedUntil !== null && this.now() < this.pausedUntil;
    }

    /** Slots for VIDEO work (code 10): the sold tier's concurrency. */
    get videoConcurrency(): number {
        return this.videoSlots;
    }

    /**
     * Code 10 (a): follow the sold tier. Takes effect for the next call to start; calls in flight
     * are never interrupted. Lowering it below the number running simply starts nothing new until
     * enough finish.
     */
    setVideoConcurrency(slots: number): void {
        this.videoSlots = clampConcurrency(slots);
        this.pump();
    }

    /**
     * Code 10 (b): `document.hidden`. While hidden the gate starts NOTHING — the retry ladder
     * parks at the gate and an abandoned tab stops submitting. Calls in flight finish normally.
     * Becoming visible resumes exactly where it stopped; no attempt is lost or duplicated.
     */
    setHidden(hidden: boolean): void {
        const next = hidden === true;
        if (next === this.hiddenFlag) return;
        this.hiddenFlag = next;
        if (!next) {
            const waiters = Array.from(this.hiddenWaiters);
            this.hiddenWaiters.clear();
            waiters.forEach(w => w());
            this.pump();
        }
    }

    get hidden(): boolean {
        return this.hiddenFlag;
    }

    /**
     * Run `task` once a slot is free and the gate is not paused. Resolves or
     * rejects with whatever the task did; a rejection frees the slot exactly
     * like a success, so one failing card cannot wedge the batch.
     *
     * `kind` (code 10): `'video'` work may use up to `videoConcurrency` slots in total; `'other'`
     * work (the default) only ever starts while fewer than `concurrency` calls are running — today's
     * behaviour for non-video batches, whatever the tier.
     */
    run<T>(task: () => Promise<T>, kind: ScheduleWorkKind = 'other'): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.queue.push({ task, kind, resolve, reject } as QueueEntry<any>);
            this.pump();
        });
    }

    /**
     * Pause every NOT-YET-STARTED call until `instantMs`. Calls already in
     * flight are deliberately left alone: their schedule request may already
     * have created a workflow server-side, and aborting it is what produces
     * orphans. Extending an existing pause wins; shortening it never does.
     */
    pauseUntil(instantMs: number): void {
        if (this.pausedUntil !== null && instantMs <= this.pausedUntil) return;
        this.pausedUntil = instantMs;
        this.armTimer();
    }

    /** Convenience for a parsed `Retry-After`: pause the batch for `delayMs`. */
    pauseFor(delayMs: number): void {
        this.pauseUntil(this.now() + Math.max(0, delayMs));
    }

    /**
     * Resolve once the shared Retry-After pause has lifted (immediately when there is none). For
     * callers that are NOT admitted through `run()` — the pre-upload endpoint resolve (code 1: a plain
     * 429 + Retry-After on `/servers/resolve`) — so N cards wait for the same instant instead of each
     * re-asking. `honourHidden` additionally waits while the tab is hidden (retry attempts only: the
     * first resolve of a freshly dropped file must not stall because the user switched tabs).
     */
    whenUnpaused(options: { honourHidden?: boolean } = {}): Promise<void> {
        return new Promise<void>(resolve => {
            const check = () => {
                if (options.honourHidden && this.hiddenFlag) {
                    this.hiddenWaiters.add(check);
                    return;
                }
                if (!this.isPaused) {
                    this.pauseWaiters.delete(check);
                    resolve();
                    return;
                }
                // Woken by the instant passing, by a later `resume()`, or (harmlessly) by both.
                this.pauseWaiters.add(check);
                this.setTimer(check, Math.max(0, this.pausedUntil! - this.now()));
            };
            check();
        });
    }

    private readonly hiddenWaiters = new Set<() => void>();
    private readonly pauseWaiters = new Set<() => void>();

    /** Lift the pause immediately (a user-pressed "Retry all"). */
    resume(): void {
        this.pausedUntil = null;
        this.disarmTimer();
        const waiters = Array.from(this.pauseWaiters);
        this.pauseWaiters.clear();
        waiters.forEach(w => w());
        this.pump();
    }

    private armTimer(): void {
        this.disarmTimer();
        if (this.pausedUntil === null) return;
        const wait = Math.max(0, this.pausedUntil - this.now());
        this.timerHandle = this.setTimer(() => {
            this.timerHandle = null;
            // The pause may have been extended while this timer was pending.
            if (this.isPaused) {
                this.armTimer();
                return;
            }
            this.pausedUntil = null;
            this.pump();
        }, wait);
    }

    private disarmTimer(): void {
        if (this.timerHandle !== null) {
            this.clearTimer(this.timerHandle);
            this.timerHandle = null;
        }
    }

    /** Total calls that may be running for a call of this kind to start. */
    private limitFor(kind: ScheduleWorkKind): number {
        return kind === 'video' ? Math.max(this.concurrency, this.videoSlots) : this.concurrency;
    }

    private pump(): void {
        if (this.hiddenFlag) return;
        if (this.isPaused) {
            this.armTimer();
            return;
        }
        if (this.pausedUntil !== null) {
            // The instant passed without the timer having fired yet.
            this.pausedUntil = null;
            this.disarmTimer();
        }

        for (; ;) {
            // FIFO among the calls that may start now. A video card is not held behind a non-video
            // card that is waiting on the smaller lane, and vice versa.
            const index = this.queue.findIndex(e => this.running < this.limitFor(e.kind));
            if (index < 0) break;
            const entry = this.queue.splice(index, 1)[0];
            this.running++;
            let started: Promise<any>;
            try {
                started = Promise.resolve(entry.task());
            } catch (error) {
                // A thunk that throws synchronously must still free its slot.
                started = Promise.reject(error);
            }
            started.then(
                value => { this.settle(entry, () => entry.resolve(value)); },
                error => { this.settle(entry, () => entry.reject(error)); },
            );
        }
    }

    private settle(_entry: QueueEntry<any>, deliver: () => void): void {
        this.running--;
        deliver();
        this.pump();
    }
}

export function createBatchScheduleGate(
    options?: BatchScheduleGateOptions,
): BatchScheduleGate {
    return new BatchScheduleGate(options);
}
